import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { PATH } from '@app/constants';
import { Phase, ProjectV2 } from '@app/interfaces';
import { DevelopmentParticipations } from '@interfaces/app';
import { PiV2PurchaseComponent } from './pi-v2-purchase/pi-v2-purchase.component';
import { UserService } from '@app/services';
import { ModalService } from '@app/services/modal/modal.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'mnf-pi-v2-card',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './pi-v2-card.component.html',
  styleUrl: './pi-v2-card.component.scss'
})
export class PiV2CardComponent implements OnChanges {

  @Input() devParts: DevelopmentParticipations;
  @Input() infoProject: ProjectV2;
  @Input() phase: Phase;
  @Input() totalPartsUsers: number;

  percentage: string | number;
  message: string;
  performanceData = [];
  percent: number;

  labelFreeParts: string = 'Proyecto en proceso de fondeo';

  restDayTotal: string;

  isScreenSmall: boolean;

  idParts: { idCardFront: string, idCardBack: string, otherDocument: string, totalParts: number };

  resaleUserPartPath = PATH.ROUTE_RESALE_SET_ON_SALE;
  resaleBuyUserPartPath = PATH.ROUTE_RESALE_MARKET;

  constructor(private router: Router, private matDialog: MatDialog, private userServ: UserService, private modalServ: ModalService, private toastServ: ToastrService) { this.onResize(); }

  @HostListener('window:resize', ['$event'])
  onResize(event?) {
    this.isScreenSmall = window.innerWidth < 1340;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.infoProject?.currentValue) {
      this.updatePerformanceData();
    }

    if (changes.phase?.currentValue) {
      this.calculateFundingStatus();
      const daysLeftToInvest = this.calculateDaysDifference(this.phase?.maxDateToInvest);
      if(daysLeftToInvest <= 0){ this.restDayTotal = 'Hoy es el último día para invertir'; }
      else{ this.restDayTotal = `Queda${daysLeftToInvest > 1 ? 'n' : ''} ${daysLeftToInvest} día${daysLeftToInvest > 1 ? 's' : ''} para invertir` }
    }
  }

  private updatePerformanceData(): void {
    if (this.infoProject?.optimisticPerformance !== undefined || this.infoProject?.estimatedPerformance !== undefined || this.infoProject?.minimumPerformance !== undefined) {
      this.performanceData = [
        { label: 'Conservador', value: `${this.infoProject?.minimumPerformance}%` },
        { label: 'Estimado', value: `${this.infoProject?.estimatedPerformance}%` },
        { label: 'Optimista', value: `${this.infoProject?.optimisticPerformance}%` }
      ];
    } else {
      this.performanceData = [
        { value: `-`, label: 'Conservador' },
        { value: `-`, label: 'Estimado' },
        { value: `-`, label: 'Optimista' }
      ];
    }
  }

  private calculateFundingStatus(): void {
    const total = Number(this.phase?.total);
    let minAmountProject = Number(this.phase?.minAmountProject);
    if (minAmountProject == 0) {
      minAmountProject = this.calculateAutoPercent(total);
    }
    const active = Number(this.phase?.active);

    let percentMinAmount = (Number(active) / Number(minAmountProject)) * 90;
    if (this?.phase?.free == 0) {
      this.percentage = 100;
      this.message = `100%`;
      this.labelFreeParts = 'Proyecto fondeado';
    } else if (total == minAmountProject) {
      this.percentage = (Number(active) / Number(total)) * 100;
      this.message = `${this.percentage.toFixed(2)}%`;
      this.labelFreeParts = `Restan <strong class='part'>${this.stringNumberToNumberWithCommas(total - active)} participaciones </strong> para invertir`;
    } else if (percentMinAmount >= 90) {
      this.percentage = 90;
      this.message = 'Mínimo alcanzado';
      this.labelFreeParts = `Restan <strong class='part'>${this.stringNumberToNumberWithCommas(total - active)} participaciones </strong> para invertir`;
      if (active >= minAmountProject) {
        let percentTotal = (Number(active) / Number(total)) * 100;
        if (percentTotal >= 95) {
          this.percentage = percentTotal;
        }
      }
    } else if (percentMinAmount <= 90) {
      this.percentage = (Number(active) / Number(minAmountProject)) * 100;
      this.message = this.percentage.toFixed(2) + '%';
      this.labelFreeParts = `Restan <strong class='part'>${minAmountProject - active} participaciones </strong> para llegar al objetivo mínimo`;
    }
  }

  private stringNumberToNumberWithCommas(value: number): string {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private isValidDateLimit(datelimit?: number): boolean {
   return typeof datelimit === 'number' && !isNaN(datelimit) && datelimit > 0;
 }

 calculateDaysDifference(datelimit?: number): number {
   const DAYS_FALLBACK = 60;
   const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
   if (!this.isValidDateLimit(datelimit)) {
     return DAYS_FALLBACK;
   }
   const currentTime = Date.now();

   const timeDifference = datelimit! - currentTime;
   if (timeDifference <= 0) {
     return 0;
   }
   const daysDifference = timeDifference / MILLISECONDS_IN_A_DAY;
   return daysDifference >= 1 ? Math.floor(daysDifference) : 0;
 }

  getProjectStateText(state: string): string {
    const stateTextMap = {
      'variable': 'Rendimiento variable',
      'fixed': 'Rendimiento fijo',
    };
    return stateTextMap[state] || 'Rendimento variable';
  }

  rightPosition(): number {
    this.percent = Number(this.percentage);
    if (this.percent < 10) {
      return -55;
    } else if (this.percent > 90) {
      return -7;
    } else {
      return -28;
    }
  }

  getSpanTranslate(): string {
    this.percent = Number(this.percentage);
    if (this.percent < 10) {
      return 'translate(-1.5rem, 1.7em) rotate(45deg)';
    } else if (this.percent > 90) {
      return 'translate(1.4rem, 1.9em) rotate(45deg)';
    } else {
      return 'translate(0.2em, 1.9em) rotate(45deg)';
    }
  }

  calculateAutoPercent(number: number) {
    return number * 0.20;
  }

  getInfoIDUser() {
    return this.userServ.getIDUser();
  }

  next(infoProject: any) {
    this.openModalPurchase(infoProject);
  }

  handleIDUserResponse(res: any, infoProject: any) {
    if ((res.idCardFront == null || res.idCardFront == undefined || res.idCardFront == '') &&
        (res.otherDocument == null || res.otherDocument == undefined || res.otherDocument == '') && res.totalParts >= 8) {
      let m = this.modalServ.modalCustom({
        img: 'https://static.monific.com/assets/assets-app/img/icon/img/request-edition.svg',
        header: '¡Ups!',
        title: 'Subir el nivel de cuenta',
        message: 'Para poder continuar con la inversión, es necesario que subas tu nivel de cuenta, para ello requerimos algún documento de identificación oficial',
        buttonText: 'Continuar',
        cancelText: 'Cancelar'
      });

      m.afterClosed().subscribe(res => {
        if (res) {
          this.openModalUploadAndCheckDocuments(infoProject);
        } else {
          this.toastServ.info('Operación cancelada', 'Aviso');
        }
      });

    } else {
      this.openModalPurchase(infoProject);
    }
  }

  openModalUploadAndCheckDocuments(infoProject: any) {
    let a = this.modalServ.openModalUploadINE();
    a.afterClosed().subscribe(res => {
      if (res) {
        this.getInfoIDUser().subscribe(newRes => {
          if ((newRes.idCardFront != null && newRes.idCardFront != undefined && newRes.idCardFront != '') ||
              (newRes.otherDocument != null && newRes.otherDocument != undefined && newRes.otherDocument != '') && newRes.totalParts >= 8) {
            this.toastServ.success('Tu cuenta ha subido de nivel', '¡Felicidades!');
            this.openModalPurchase(infoProject);
          } else {
            this.toastServ.info('Es necesario subir algún documento de identificación', 'Aviso');
            this.openModalUploadAndCheckDocuments(infoProject);
          }
        });
      } else {
        this.toastServ.info('Es necesario subir algún documento de identificación', 'Aviso');
        this.openModalUploadAndCheckDocuments(infoProject);
      }
    });
  }

  openModalPurchase(infoProject: any) {
    let m = this.matDialog.open(PiV2PurchaseComponent, {
      width: '100%',
      maxWidth: '610px',
      panelClass: 'mnf-modal-container',
      disableClose: true,
    });

    m.componentInstance.infoProject = infoProject;
  }

  getLetterClass(): string{
    let letter: string = this.infoProject?.ratingProject;
    const letterPrefix = letter?.match(/^[A-Za-z]+/)?.[0] || '';
    const letterMap: { [key: string]:string } = {
      'B': 'rb-ms',
      'MB': 'mb-ms',
      'M': 'rm-ms',
      'A': 'ra-ms',
    };
    return letterMap[letterPrefix] || '';
  }
}
